import { WaitingCriteriaOptions } from '@forwardflow/helpers/utils/campaign'
import { SelectField } from '@forwardflow/ui'
import { Slider } from 'antd'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { SpeedToLeadGraph } from './SpeedToLeadGraph'
import {
   useGetFubStages,
   useGetFubTags,
} from '@forwardflow/hooks/customHooks/stageMapping'
import {
   useGetActiveLeadSourceDetails,
   useGetDashboardData,
} from '@forwardflow/hooks/customHooks'
import { useParams } from 'react-router-dom'

interface ContactBothProps {
   onTagSelect?: (selectedTag: string) => void
   onStageSelect?: (selectedStage: string) => void
   onTimePeriodSelect?: (selectedTimePeriod: string) => void
}
function useDebounce(callback: (...args: any[]) => void, delay: number) {
   const timer = useRef<NodeJS.Timeout | null>(null)

   const debouncedCallback = useCallback(
      (...args: any[]) => {
         if (timer.current) {
            clearTimeout(timer.current)
         }
         timer.current = setTimeout(() => {
            callback(...args)
         }, delay)
      },
      [callback, delay]
   )

   return debouncedCallback
}

export const ContactBoth = ({
   onTagSelect,
   onStageSelect,
   onTimePeriodSelect,
}: ContactBothProps) => {
   const { data: dashboardData, isLoading: isDashboardDataLoading } =
      useGetDashboardData()
   const { title } = useParams()
   const { data: firstToContactData } = useGetActiveLeadSourceDetails(
      title as string
   )

   // Define the mapping between buffer time in minutes and slider values
   const bufferTimeToSliderMap = useMemo(
      () => ({
         0: 1, // 0 min
         1: 2, // 1 min
         2: 3, // 2 min
         3: 4, // 2 min
         5: 5, // 5 min
         10: 6, // 10 min
         30: 7, // 30 min
         60: 8, // 1 hr
         300: 9, // 5 hr
      }),
      []
   )

   const marks = useMemo(
      () => ({
         1: 'Immediate',
         2: '1 min',
         3: '2 min',
         4: '3 min',
         5: '5 min',
         6: '10 min',
         7: '30 min',
         8: '1 hr',
         9: '5 hr',
      }),
      []
   )

   const getColorBasedOnSliderValue = (value: number): string => {
      return value >= 7 ? 'text-[#F00]' : ''
   }

   // Convert buffer time to slider value
   const convertBufferTimeToSliderValue = (bufferTime: number): number => {
      // Find the exact match or the closest value
      const exactMatch =
         bufferTimeToSliderMap[bufferTime as keyof typeof bufferTimeToSliderMap]
      if (exactMatch) return exactMatch

      // If no exact match, find the closest value
      const bufferTimes = Object.keys(bufferTimeToSliderMap).map(Number)
      const closest = bufferTimes.reduce((prev, curr) =>
         Math.abs(curr - bufferTime) < Math.abs(prev - bufferTime) ? curr : prev
      )

      return bufferTimeToSliderMap[
         closest as keyof typeof bufferTimeToSliderMap
      ]
   }

   // Initialize slider value based on buffer time
   const initialBufferTime =
      firstToContactData?.data?.firstToContact?.bufferTime ?? 0
   const [sliderValue, setSliderValue] = useState(
      convertBufferTimeToSliderValue(initialBufferTime)
   )

   const [waitingCriteria, setWaitingCriteria] = useState('Time Period')

   useEffect(() => {
      if (firstToContactData?.data?.firstToContact?.bufferTime !== undefined) {
         // Update slider value when data changes
         setSliderValue(
            convertBufferTimeToSliderValue(
               firstToContactData.data.firstToContact.bufferTime
            )
         )
      }

      setWaitingCriteria('Time Period')
      onTimePeriodSelect?.(
         firstToContactData?.data?.firstToContact?.bufferTime !== undefined
            ? marks[
                 convertBufferTimeToSliderValue(
                    firstToContactData.data.firstToContact.bufferTime
                 ) as keyof typeof marks
              ]
            : 'Immediate'
      )
   }, [firstToContactData])

   const { data: tags } = useGetFubTags({
      page: 1,
      limit: 10000,
   })
   const tagsOptions = tags?.data?.map((item: any) => ({
      label: item?.tagName,
      value: item?.tagName,
   }))
   const { data: Stages } = useGetFubStages({
      page: 1,
      limit: 10000,
   })
   const StagesOptions = Stages?.data?.map((item: any) => ({
      label: item?.stageName,
      value: item?.stageName,
   }))

   const debouncedOnChange = useDebounce((value: number) => {
      setSliderValue(value)
      if (onTimePeriodSelect) {
         onTimePeriodSelect(marks[value as keyof typeof marks])
      }
   }, 300) // Adjust the delay as needed

   const renderContent = () => {
      switch (waitingCriteria) {
         case 'Time Period':
            return (
               <>
                  <div className="flex items-center gap-5 px-10 py-6">
                     <p className="text-[14px] font-medium">
                        Waiting Time Period
                     </p>
                     <div
                        className={`text-center w-20 ${getColorBasedOnSliderValue(
                           sliderValue
                        )}`}
                     >
                        {marks[sliderValue as keyof typeof marks]}
                     </div>
                     <Slider
                        value={sliderValue}
                        min={1}
                        max={9}
                        step={null}
                        marks={marks}
                        defaultValue={convertBufferTimeToSliderValue(
                           firstToContactData?.data?.firstToContact
                              ?.bufferTime || 1
                        )}
                        tooltip={{
                           formatter: (value: number | undefined) =>
                              value !== undefined
                                 ? marks[value as keyof typeof marks]
                                 : '',
                        }}
                        className="w-[400px] stepper-slider !my-0"
                        onChange={debouncedOnChange}
                     />
                  </div>
                  <div className="flex justify-start px-10 py-6">
                     <SpeedToLeadGraph
                        sliderValue={sliderValue}
                        speedToLead={dashboardData?.speedToLead}
                        isStatsLoading={isDashboardDataLoading}
                     />
                  </div>
               </>
            )
         case 'Tag':
            return (
               <div className="flex items-center w-full gap-5 px-10 py-6">
                  <div className="flex items-center w-full gap-5 ">
                     <label className="text-[14px] font-medium">
                        Select Tag
                     </label>
                     <SelectField
                        showSearch
                        className="ff-inline-select-border-4"
                        formItemClassName="mb-0 w-[200px]"
                        placeholder="Select Tag"
                        options={tagsOptions}
                        defaultValue={
                           firstToContactData?.data?.firstToContact?.tags?.[0]
                        }
                        onChange={(value: string) => {
                           if (onTagSelect) {
                              onTagSelect(value)
                           }
                        }}
                     />
                  </div>
               </div>
            )
         case 'Stage':
            return (
               <div className="flex items-center w-full gap-5 px-10 py-6">
                  <div className="flex items-center w-full gap-5 ">
                     <label className="text-[14px] font-medium">
                        Select Stage
                     </label>
                     <SelectField
                        showSearch
                        className="ff-inline-select-border-4"
                        formItemClassName="mb-0 w-[200px]"
                        placeholder="Select Stage"
                        options={StagesOptions}
                        defaultValue={
                           firstToContactData?.data?.firstToContact?.stages?.[0]
                        }
                        onChange={(value: string) => {
                           if (onStageSelect) {
                              onStageSelect(value)
                           }
                        }}
                     />
                  </div>
               </div>
            )
         default:
            return null
      }
   }

   return (
      <>
         <div className="items-center hidden gap-5 px-10 pt-6 ">
            <label className="text-[14px] font-medium">Waiting Criteria</label>
            <SelectField
               className="ff-inline-select-border-4"
               formItemClassName="mb-0 w-[200px]"
               value={waitingCriteria}
               options={WaitingCriteriaOptions}
               onChange={(value) => setWaitingCriteria(value)}
            />
         </div>
         <p className="text-[12px] text-white/70 pb-5 border-b border-black/20 px-10 py-6">
            Forward Flow will wait for the Agents to contact the leads. If the
            agent fails to contact according to the set Waiting Period, Forward
            Flow will start contacting the lead.
         </p>
         {renderContent()}
      </>
   )
}
